/**
 * El catálogo de tipos de campo de **datos** (plan del CMS dinámico, §5): lo que un cliente
 * monta en un grupo de campos (ACF) y rellena en cada documento. Es un solo catálogo para
 * tres consumidores —el núcleo que valida, el panel que pinta y la IA que planifica y diseña—
 * porque si cada uno llevara su copia acabarían diciendo cosas distintas (CLAUDE.md §24).
 *
 * Los valores de `tipo` serán un enum de Postgres y de GraphQL: ni eñes, ni espacios, ni
 * guiones, ni nada que empiece por un dígito (§28). Las **opciones** de un selector, en
 * cambio, las escribe el cliente y nunca salen de aquí a un enum: viven en el `jsonb` y se
 * validan contra su definición (§7).
 */

export const TIPOS_DE_DATO = [
  'texto',
  'area',
  'numero',
  'email',
  'url',
  'telefono',
  'fecha',
  'hora',
  'fechaHora',
  'booleano',
  'selector',
  'multiselector',
  'radio',
  'casillas',
  'color',
  'textoRico',
  'imagen',
  'galeria',
  'archivo',
  'relacion',
  'grupo',
  'repetidor',
  'lugar',
  'mapa',
] as const
export type TipoDeDato = (typeof TIPOS_DE_DATO)[number]

export const NOMBRES: Record<TipoDeDato, string> = {
  texto: 'Texto corto',
  area: 'Texto largo',
  numero: 'Número',
  email: 'Correo electrónico',
  url: 'Dirección web',
  telefono: 'Teléfono',
  fecha: 'Fecha',
  hora: 'Hora',
  fechaHora: 'Fecha y hora',
  booleano: 'Sí / no',
  selector: 'Una opción (desplegable)',
  multiselector: 'Varias opciones (desplegable)',
  radio: 'Una opción (botones)',
  casillas: 'Varias opciones (casillas)',
  color: 'Color',
  textoRico: 'Texto con formato',
  imagen: 'Imagen',
  galeria: 'Galería de imágenes',
  archivo: 'Archivo',
  relacion: 'Relación con otro contenido',
  grupo: 'Grupo de campos',
  repetidor: 'Lista repetible',
  lugar: 'Lugar oficial',
  mapa: 'Ubicación en el mapa',
}

/** La rejilla de seis columnas, la misma que la de los formularios (§9): un campo dice cuánto ocupa. */
export const ANCHOS = ['completo', 'mitad', 'tercio', 'dos-tercios'] as const
export type Ancho = (typeof ANCHOS)[number]
export const COLUMNAS_DE_ANCHO: Record<Ancho, number> = { completo: 6, mitad: 3, tercio: 2, 'dos-tercios': 4 }

/** Los que van siempre enteros: un grupo, una lista o un editor parten la ficha de lado a lado. */
export const SIN_ANCHO: TipoDeDato[] = ['grupo', 'repetidor', 'textoRico', 'galeria']

/**
 * El ancho de un campo: el suyo, o entero si no lo dice (o si es de los que van siempre
 * enteros). `@mc/formularios` lo reutiliza con su propia lista de los que van enteros.
 */
export const anchoDe = (campo: { tipo: string; ancho?: null | string }, sinAncho: readonly string[] = SIN_ANCHO): Ancho =>
  !sinAncho.includes(campo.tipo) && (ANCHOS as readonly string[]).includes(String(campo.ancho)) ? (campo.ancho as Ancho) : 'completo'

/**
 * Una clave es un identificador, no una etiqueta: acaba siendo `doc.datos.horarioDeVisita`
 * en el tema, y ahí un espacio o un guion obligarían a escribir `datos['horario-de-visita']`.
 */
export const CLAVE = /^[a-z][A-Za-z0-9]{0,39}$/

/** Lo que ya significa algo en un documento o en JavaScript: un dato no puede llamarse así. */
export const RESERVADAS = new Set(['id', 'cliente', 'sitio', 'slug', 'titulo', 'seo', 'datos', 'extras', 'terminos', 'tipo', 'constructor', 'prototype', '__proto__'])

/** El valor de una opción también es un identificador: la etiqueta es libre, el valor no. */
export const VALOR_DE_OPCION = /^[a-z0-9][a-z0-9_-]{0,63}$/

export const COLECCIONES_DE_RELACION = ['contenidos', 'productos', 'servicios', 'paginas', 'entradas', 'usuarios', 'terminos'] as const
export type ColeccionDeRelacion = (typeof COLECCIONES_DE_RELACION)[number]

export const FAMILIAS_DE_ARCHIVO = ['imagen', 'documento', 'cualquiera'] as const
export type FamiliaDeArchivo = (typeof FAMILIAS_DE_ARCHIVO)[number]

export const VISIBILIDADES = ['publico', 'privado', 'cualquiera'] as const
export type Visibilidad = (typeof VISIBILIDADES)[number]

/** Hasta dónde se pide un lugar: el primer nivel de su país, el segundo o el último (como en formularios). */
export const NIVELES_DE_LUGAR = ['primero', 'segundo', 'ultimo'] as const
export type NivelDeLugar = (typeof NIVELES_DE_LUGAR)[number]

export type Opcion = { valor: string; etiqueta: string }

type Comun = {
  clave: string
  etiqueta: string
  ayuda?: null | string
  obligatorio?: boolean | null
  /** Cuánto ocupa en la fila (`anchoDe`). Entero si no lo dice. */
  ancho?: null | Ancho
  /** Se podrá filtrar por él en el archivo del tipo (el núcleo lo indexa). */
  filtrable?: boolean | null
}

export type CampoTexto = Comun & { tipo: 'texto' | 'area'; minimo?: null | number; maximo?: null | number }
export type CampoNumero = Comun & { tipo: 'numero'; minimo?: null | number; maximo?: null | number; decimales?: null | number; unidad?: null | string; esMoneda?: boolean | null }
export type CampoSimple = Comun & { tipo: 'email' | 'url' | 'telefono' | 'fecha' | 'hora' | 'fechaHora' | 'booleano' | 'color' | 'textoRico' | 'mapa' }
export type CampoConOpciones = Comun & { tipo: 'selector' | 'multiselector' | 'radio' | 'casillas'; opciones: Opcion[] }
export type CampoMedio = Comun & { tipo: 'imagen' | 'archivo'; tipos?: null | FamiliaDeArchivo[]; visibilidad?: null | Visibilidad }
export type CampoGaleria = Comun & { tipo: 'galeria'; maximo?: null | number }
export type CampoRelacion = Comun & {
  tipo: 'relacion'
  /** `tipo` solo con `contenidos` (el slug del tipo de contenido); `taxonomia` solo con `terminos`. */
  destino: { coleccion: ColeccionDeRelacion; tipo?: null | string; taxonomia?: null | string }
  multiple?: boolean | null
}
/** Dentro de un grupo no cabe ni otro grupo ni un repetidor: un nivel. */
export type CampoGrupo = Comun & { tipo: 'grupo'; campos: CampoDeDato[] }
/** Dentro de un repetidor cabe todo menos otro repetidor: dos niveles como mucho, como en formularios. */
export type CampoRepetidor = Comun & { tipo: 'repetidor'; campos: CampoDeDato[]; minimo?: null | number; maximo?: null | number }
export type CampoLugar = Comun & { tipo: 'lugar'; paises?: null | string[]; nivel: NivelDeLugar }

export type CampoDeDato = CampoTexto | CampoNumero | CampoSimple | CampoConOpciones | CampoMedio | CampoGaleria | CampoRelacion | CampoGrupo | CampoRepetidor | CampoLugar

/**
 * La forma del valor guardado de cada tipo, en `datos` (`jsonb`):
 *
 * | tipo | valor |
 * |---|---|
 * | texto, area, email, url, telefono | `string` |
 * | numero | `number` (con los decimales que admite el campo) |
 * | fecha | `'2026-03-01'` |
 * | hora | `'09:30'` (24 h) |
 * | fechaHora | ISO 8601 en UTC (`'2026-03-01T14:30:00.000Z'`) |
 * | booleano | `boolean` |
 * | selector, radio | el `valor` de una opción |
 * | multiselector, casillas | lista de `valor` de opciones, sin repetir |
 * | color | `'#rrggbb'` en minúsculas |
 * | textoRico | el JSON de Lexical (`{ root: { … } }`), 64 KB como mucho |
 * | imagen, archivo | `number`: el id del medio |
 * | galeria | `{ medio: number, pie?: string }[]` |
 * | relacion | `{ coleccion, id }`, o una lista si `multiple` |
 * | grupo | un objeto con los valores de sus campos |
 * | repetidor | una lista de esos objetos |
 * | lugar | `{ pais, codigo, camino }` (el camino lo escribe el servidor con los nombres oficiales) |
 * | mapa | `{ direccion, lat, lng }` |
 */
export type Referencia = { coleccion: string; id: number }
export type ValorDeGaleria = { medio: number; pie?: string }
export type ValorDeLugar = { pais: string; codigo: null | string; camino: string; texto?: string }
export type ValorDeMapa = { direccion: string; lat: number; lng: number }
export type TextoRico = { root: Record<string, unknown> }

export type ValorDe<T extends CampoDeDato> = T extends { tipo: 'numero' }
  ? number
  : T extends { tipo: 'booleano' }
    ? boolean
    : T extends { tipo: 'multiselector' | 'casillas' }
      ? string[]
      : T extends { tipo: 'textoRico' }
        ? TextoRico
        : T extends { tipo: 'imagen' | 'archivo' }
          ? number
          : T extends { tipo: 'galeria' }
            ? ValorDeGaleria[]
            : T extends { tipo: 'relacion' }
              ? Referencia | Referencia[]
              : T extends { tipo: 'grupo' }
                ? Record<string, unknown>
                : T extends { tipo: 'repetidor' }
                  ? Record<string, unknown>[]
                  : T extends { tipo: 'lugar' }
                    ? ValorDeLugar
                    : T extends { tipo: 'mapa' }
                      ? ValorDeMapa
                      : string

/** La clave es la ruta con puntos e índices desde la raíz de `datos`: `caracteristicas.2.nombre`. */
export type Problema = { clave: string; mensaje: string }

export const esContenedor = (campo: CampoDeDato): campo is CampoGrupo | CampoRepetidor => campo.tipo === 'grupo' || campo.tipo === 'repetidor'
